import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { ContactForm } from "@/components/ContactForm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Phone, Mail, MapPin, Clock, PlayCircle } from "lucide-react";
import { LOCAL_BUSINESS_SCHEMA } from "@/lib/entitySchema";

const searchSchema = z.object({
  website: z.string().trim().max(500).optional().catch(undefined),
  email: z.string().trim().max(255).optional().catch(undefined),
  message: z.string().trim().max(1500).optional().catch(undefined),
  topic: z.string().trim().max(64).optional().catch(undefined),
  quote: z.string().max(4000).optional().catch(undefined),
});

export const Route = createFileRoute("/contact")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
        { name: "description", content: "Talk to the owner. Free 20-minute Colorado marketing strategy call — web design, SEO, PPC. Colorado Springs HQ, statewide service. Reply within 24 hours." },
      { title: "Contact Colorado Web Impressions | Free Strategy Call" },
      {
        name: "description",
        content:
          "Book a free 30-minute strategy call with Colorado Web Impressions. We'll audit your marketing and show you how to grow your Colorado business.",
      },
      { property: "og:title", content: "Contact Colorado Web Impressions | Free Strategy Call" },
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/contact" }],
    scripts: [
      // LocalBusiness reference — pairs the contact page with the business entity
      // so contact data structured signals are first-class on this page.
      {
        type: "application/ld+json",
        children: JSON.stringify(LOCAL_BUSINESS_SCHEMA),
      },
      // ContactPage schema — explicitly tells crawlers this is the canonical
      // contact entry point for the business entity defined in __root.tsx.
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "@id": "https://coloradowebimpressions.com/contact#webpage",
          url: "https://coloradowebimpressions.com/contact",
          name: "Contact Colorado Web Impressions",
          description:
            "Talk directly with the owner. Phone, email, and free strategy call booking for Colorado businesses.",
          inLanguage: "en-US",
          isPartOf: { "@id": "https://coloradowebimpressions.com/#org" },
          about: { "@id": "https://coloradowebimpressions.com/#organization" },
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://coloradowebimpressions.com/" },
              { "@type": "ListItem", position: 2, name: "Contact", item: "https://coloradowebimpressions.com/contact" },
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "VideoObject",
          name: "About Colorado Web Impressions — Why We're Different",
          description:
            "A short introduction to Colorado Web Impressions from owner Chris Heidlebaugh — what makes our family-owned, in-house Colorado marketing agency different.",
          thumbnailUrl: "https://i.ytimg.com/vi/t3no84uGrms/maxresdefault.jpg",
          uploadDate: "2024-01-01",
          embedUrl: "https://www.youtube.com/embed/t3no84uGrms",
          contentUrl: "https://www.youtube.com/watch?v=t3no84uGrms",
          publisher: {
            "@type": "Organization",
            name: "Colorado Web Impressions",
            logo: {
              "@type": "ImageObject",
              url: "https://coloradowebimpressions.com/logo.png",
            },
          },
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Contact Our Colorado Marketing Agency"
        title={
          <>
            Let's grow your <span className="text-gradient-gold">Colorado business</span>
          </>
        }
        hideMeetChris
      >
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href="tel:+17194193935"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-8 py-4 text-base font-bold uppercase tracking-wider text-secondary shadow-elegant transition-all hover:opacity-90 hover:shadow-lg"
           aria-label="Call (719) 419-3935">
            <Phone className="h-5 w-5" />
            Call (719) 419-3935
          </a>
          <button
            type="button"
            onClick={() => setVideoOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-brand-gold/60 bg-transparent px-8 py-4 text-base font-bold uppercase tracking-wider text-brand-gold transition-all hover:bg-brand-gold/10"
          >
            <PlayCircle className="h-5 w-5" />
            Watch Intro Video
          </button>
        </div>
      </PageHero>

      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent className="max-w-4xl border-white/10 bg-brand-black p-0 sm:p-0">
          <DialogTitle className="sr-only">
            About Colorado Web Impressions — Meet Chris Heidlebaugh
          </DialogTitle>
          <div className="relative aspect-video w-full overflow-hidden rounded-lg">
            {videoOpen && (
              <iframe
                src="https://www.youtube-nocookie.com/embed/t3no84uGrms?rel=0&modestbranding=1&autoplay=1"
                title="About Colorado Web Impressions — Meet Chris Heidlebaugh"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute inset-0 h-full w-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>


      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 lg:grid-cols-5 lg:px-8">
          <div className="lg:col-span-3 lg:order-1">
            <div className="rounded-3xl border border-border bg-card p-6 shadow-elegant sm:p-8">
              <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
                Request A Quote / Proposal
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Tell us a little about yourself, your project, and how we can help you.
              </p>
              <div className="mt-6">
                <ContactForm />
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 lg:order-2">
            <h2 className="text-3xl font-extrabold text-foreground">Talk To The Owner Directly</h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              No salespeople. No bots. No call centers. An actual human being. In fact, you will talk
              with the owner Chris Heidlebaugh.
            </p>

            <div className="mt-8 space-y-5">
              {[
                { icon: Phone, label: "Call us", value: "(719) 419-3935", href: "tel:+17194193935" },
                {
                  icon: Mail,
                  label: "Email us",
                  value: "info@coloradowebimpressions.com",
                  href: "mailto:info@coloradowebimpressions.com",
                },
                { icon: MapPin, label: "Headquarters", value: "Colorado Springs, CO" },
                { icon: Clock, label: "Hours", value: "Mon–Fri, 9am–4pm MT" },
              ].map((c) => (
                <div key={c.label} className="flex items-start gap-4">
                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-secondary text-brand-gold">
                    <c.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      {c.label}
                    </div>
                    {c.href ? (
                      <a
                        href={c.href}
                        className="text-lg font-semibold text-foreground hover:text-brand-gold-deep"
                      >
                        {c.value}
                      </a>
                    ) : (
                      <div className="text-lg font-semibold text-foreground">{c.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 overflow-hidden rounded-xl border border-border shadow-card">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3681.334438163903!2d-104.76531179999999!3d38.9359976!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87132ece8b07b43f%3A0xc60ca26c53d71ee9!2sColorado%20Web%20Impressions!5e1!3m2!1sen!2sus!4v1777312054867!5m2!1sen!2sus"
                width="400"
                height="300"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Colorado Web Impressions location map"
                className="block w-full"
              />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
